import type { CSSProperties, ReactNode } from "react";

/**
 * Sprint 18 Wave A — shared error-state primitive.
 *
 * Replaces inline `ErrorState()` helpers across apps/web. Pages compose
 * this when a fetch fails or a permission gate denied access.
 *
 * Accessibility:
 *   - Wrapped in `role="alert"` so the failure is announced
 *     immediately on render. Pair with a contained Retry CTA so the
 *     announcement is actionable.
 *   - The decorative glyph is `aria-hidden`.
 *
 * Visual rules:
 *   - Uses `--gt-sys-danger` for the heading icon when severity is
 *     `error` (default). Use `severity: "warn"` for non-blocking
 *     degraded states (no retry needed); falls back to muted text.
 */
export type ErrorStateSeverity = "error" | "warn";

export interface ErrorStateProps {
  /** Visible heading. */
  title: string;
  /** Optional message body. */
  body?: ReactNode;
  /** Severity tone (default `error`). */
  severity?: ErrorStateSeverity;
  /** Optional retry / dismiss / "back to safety" affordance. */
  action?: ReactNode;
  /** Optional decorative glyph (defaults to ⚠︎). */
  icon?: ReactNode;
  padBlock?: CSSProperties["paddingBlock"];
  style?: CSSProperties;
}

export function ErrorState({
  title,
  body,
  severity = "error",
  action,
  icon,
  padBlock,
  style,
}: ErrorStateProps) {
  const accent =
    severity === "error"
      ? "var(--gt-sys-danger)"
      : "var(--gt-sys-warning, var(--gt-sys-text-muted))";
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--gt-sys-space-3)",
        paddingBlock: padBlock ?? "var(--gt-sys-space-6)",
        textAlign: "center",
        ...style,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 40, color: accent }}>
        {icon ?? "⚠︎"}
      </span>
      <strong style={{ fontSize: "var(--gt-sys-font-size-md)" }}>{title}</strong>
      {body ? (
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-sm)",
            maxInlineSize: 420,
          }}
        >
          {body}
        </span>
      ) : null}
      {action ? <div>{action}</div> : null}
    </div>
  );
}
